import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'
import { Check, FileQuestion, Film, HardDrive, Image as ImageIcon, Info, Library, Lock, Pencil, Search, Trash2, Upload, X } from 'lucide-react'
import { cgAssetsApi } from '../../lib/api.js'
import type { CgAsset } from '../../types/index.js'
import { Spinner } from '../common/Spinner.js'

interface Props {
  active?: boolean
  channelId: string
  assetsVersion: number
  protectedAssetIds: string[]
  onAssetsChanged: () => void
}

interface AssetTileProps {
  asset: CgAsset
  protectedByDraft: boolean
  deleting: boolean
  renaming: boolean
  onDelete: (asset: CgAsset) => void
  onRename: (asset: CgAsset, name: string) => void
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024
const MAX_VIDEO_SIZE = 1024 * 1024 * 1024

function isVideoAsset(asset: CgAsset): boolean {
  return asset.mimeType.startsWith('video/')
}

function supportedUpload(file: File): 'IMAGE' | 'VIDEO' | null {
  const extension = file.name.split('.').pop()?.toLowerCase()
  if (file.type.startsWith('image/')) return 'IMAGE'
  if (file.type === 'video/webm' || extension === 'webm') return 'VIDEO'
  if (['video/quicktime', 'video/mov', 'application/octet-stream', ''].includes(file.type) && extension === 'mov') return 'VIDEO'
  return null
}

function formatAssetSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

function formatAssetFolder(relativePath: string): string {
  return relativePath
    .replace(/\\/g, '/')
    .split('/')
    .slice(2, -1)
    .map(part => part.toLowerCase() === 'logos' ? 'Logos' : part)
    .join('/')
}

const AssetTile = memo(function AssetTile({ asset, protectedByDraft, deleting, renaming, onDelete, onRename }: AssetTileProps) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(asset.name)
  const inputRef = useRef<HTMLInputElement>(null)

  const deleteAsset = useCallback(() => onDelete(asset), [asset, onDelete])
  const startEdit = useCallback(() => { setValue(asset.name); setEditing(true) }, [asset.name])
  const cancelEdit = useCallback(() => setEditing(false), [])
  const submitEdit = useCallback(() => {
    const next = value.trim()
    if (!next || next === asset.name) { setEditing(false); return }
    onRename(asset, next)
    setEditing(false)
  }, [asset, onRename, value])
  const onInputKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') { event.preventDefault(); submitEdit() }
    else if (event.key === 'Escape') { event.preventDefault(); cancelEdit() }
  }, [cancelEdit, submitEdit])

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select() }
  }, [editing])

  const usageLabel = protectedByDraft
    ? 'Utilise dans le brouillon'
    : asset.usageCount
    ? `${asset.usageCount} utilisation${asset.usageCount > 1 ? 's' : ''}`
    : 'Disponible'
  const protectedAsset = asset.usageCount > 0 || protectedByDraft
  const video = isVideoAsset(asset)
  const folder = formatAssetFolder(asset.relativePath)

  return (
    <article className={`cg-library-asset${asset.missing ? ' is-missing' : ''}`}>
      <div className="cg-library-asset-preview">
        {/* Un fichier disparu du dossier n'a pas de preview a montrer : afficher
            l'URL casserait la vignette sans dire pourquoi. */}
        {asset.missing
          ? <FileQuestion size={22} aria-hidden="true" />
          : video
          ? <video src={asset.url} autoPlay muted loop playsInline aria-label={asset.name} />
          : <img src={asset.url} alt="" />}
        <span className="cg-library-asset-kind">{video ? <Film size={11} /> : <ImageIcon size={11} />}{video ? 'VIDEO' : 'IMAGE'}</span>
      </div>
      <div className="cg-library-asset-body">
        {editing ? (
          <input
            ref={inputRef}
            className="cg-library-asset-rename-input"
            value={value}
            disabled={renaming}
            onChange={event => setValue(event.target.value)}
            onKeyDown={onInputKeyDown}
            aria-label="Nouveau nom de l'asset"
          />
        ) : (
          <strong title={asset.name}>{asset.name}</strong>
        )}
        {folder && <span>{folder}</span>}
        <span>{asset.missing ? 'Fichier introuvable' : `${formatAssetSize(asset.sizeBytes)} · ${usageLabel}`}</span>
      </div>
      {editing ? (
        <>
          <button type="button" className="cg-icon-button is-cancel" onClick={cancelEdit} disabled={renaming} title="Annuler" aria-label="Annuler le renommage"><X size={13} /></button>
          <button type="button" className="cg-icon-button is-primary is-confirm" onClick={submitEdit} disabled={renaming || !value.trim()} title="Valider le nouveau nom" aria-label="Valider le nouveau nom"><Check size={13} /></button>
        </>
      ) : (
        <>
          <button type="button" className="cg-icon-button is-rename" onClick={startEdit} disabled={renaming} title="Renommer" aria-label={`Renommer ${asset.name}`}><Pencil size={13} /></button>
          <button
            type="button"
            className="cg-icon-button is-danger"
            onClick={deleteAsset}
            disabled={protectedAsset || deleting}
            title={protectedAsset ? 'Suppression bloquee : asset utilise' : 'Supprimer definitivement'}
            aria-label={protectedAsset ? 'Suppression bloquee : asset utilise' : `Supprimer ${asset.name}`}
          >{protectedAsset ? <Lock size={13} /> : <Trash2 size={13} />}</button>
        </>
      )}
    </article>
  )
})

export const CgAssetsLibrary = memo(function CgAssetsLibrary({ active = true, channelId, assetsVersion, protectedAssetIds, onAssetsChanged }: Props) {
  const [assets, setAssets] = useState<CgAsset[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const loadAssets = useCallback(async () => {
    setLoading(true)
    try {
      setAssets(await cgAssetsApi.list(channelId))
    } catch (error: any) {
      setMessage(error.message ?? 'Chargement impossible')
    } finally {
      setLoading(false)
    }
  }, [channelId])

  useEffect(() => { if (active) void loadAssets() }, [active, assetsVersion, loadAssets])

  const openImport = useCallback(() => inputRef.current?.click(), [])

  const importAsset = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const kind = supportedUpload(file)
    if (!kind) {
      setMessage('Formats acceptes : images, WebM et MOV ProRes 4444 alpha')
      return
    }
    const maxSize = kind === 'VIDEO' ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE
    if (file.size > maxSize) {
      setMessage(kind === 'VIDEO' ? 'Video trop lourde (1 Go maximum)' : 'Image trop lourde (10 Mo maximum)')
      return
    }
    setUploading(true)
    setMessage(file.name.toLowerCase().endsWith('.mov') ? 'Conversion du MOV 4444 en WebM alpha...' : '')
    try {
      const asset = await cgAssetsApi.upload(channelId, file)
      setAssets(current => [asset, ...current])
      onAssetsChanged()
      setMessage('Asset importe')
    } catch (error: any) {
      setMessage(error.message ?? 'Import impossible')
    } finally {
      setUploading(false)
    }
  }, [channelId, onAssetsChanged])

  const deleteAsset = useCallback(async (asset: CgAsset) => {
    if (asset.usageCount > 0 || protectedAssetIds.includes(asset.id)) {
      setMessage('Suppression bloquee : cet asset est utilise dans un habillage')
      return
    }
    if (!window.confirm(`Supprimer definitivement « ${asset.name} » ?`)) return
    setDeletingId(asset.id)
    setMessage('')
    try {
      await cgAssetsApi.delete(asset.id)
      setAssets(current => current.filter(item => item.id !== asset.id))
      onAssetsChanged()
      setMessage('Asset supprime definitivement')
    } catch (error: any) {
      setMessage(error.message ?? 'Suppression impossible')
      await loadAssets()
    } finally {
      setDeletingId(null)
    }
  }, [loadAssets, onAssetsChanged, protectedAssetIds])

  const renameAsset = useCallback(async (asset: CgAsset, name: string) => {
    setRenamingId(asset.id)
    setMessage('')
    try {
      const updated = await cgAssetsApi.rename(asset.id, name)
      setAssets(current => current.map(item => item.id === updated.id ? updated : item))
      onAssetsChanged()
      setMessage('Asset renomme')
    } catch (error: any) {
      setMessage(error.message ?? 'Renommage impossible')
    } finally {
      setRenamingId(null)
    }
  }, [onAssetsChanged])

  const filteredAssets = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('fr-FR')
    return normalized ? assets.filter(asset => asset.name.toLocaleLowerCase('fr-FR').includes(normalized)) : assets
  }, [assets, query])
  const totalSize = useMemo(() => assets.reduce((sum, asset) => sum + asset.sizeBytes, 0), [assets])

  return (
    <section className="cg-assets-library">
      <header className="cg-assets-toolbar">
        <div className="cg-assets-summary">
          <Library size={16} />
          <strong>Bibliotheque medias</strong>
          <span>{assets.length} asset{assets.length > 1 ? 's' : ''}</span>
          <span><HardDrive size={12} />{formatAssetSize(totalSize)}</span>
        </div>
        <label className="cg-assets-search">
          <Search size={14} aria-hidden="true" />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Rechercher" aria-label="Rechercher un asset" />
        </label>
        <button type="button" className="cg-button is-primary" onClick={openImport} disabled={uploading}>
          <Upload size={14} />{uploading ? 'Traitement...' : 'Importer'}
        </button>
        {message && <span className="cg-inline-message"><Info size={13} />{message}</span>}
      </header>

      <div className="cg-assets-content">
        {active && filteredAssets.map(asset => <AssetTile key={asset.id} asset={asset} protectedByDraft={protectedAssetIds.includes(asset.id)} deleting={deletingId === asset.id} renaming={renamingId === asset.id} onDelete={deleteAsset} onRename={renameAsset} />)}
        {!loading && filteredAssets.length === 0 && (
          <div className="cg-assets-empty"><Library size={24} /><span>{query ? 'Aucun resultat' : 'Aucun asset'}</span></div>
        )}
        {loading && <div className="cg-assets-empty"><Spinner /> <span>Chargement...</span></div>}
      </div>
      <input ref={inputRef} className="cg-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,video/webm,video/quicktime,.mov" onChange={importAsset} />
    </section>
  )
})
